import React from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {BedConfiguration, Bedroom} from '@findhotel/sapi'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/src/themes'

import {DotSeparatedList} from '../DotSeperatedList'
import {getBedTypeIcon} from './getBedTypeIcon'
import {groupBedTypesByConfig} from './utils'

const StyledIcon = styled(Icon)<{
  withMargin: boolean
  withNegativeMargin?: boolean
}>`
  display: flex;
  align-items: center;
  margin-right: ${({withMargin}) =>
    withMargin ? cssTheme.layout.spacing.s250 : 0};
  margin-left: ${({withNegativeMargin}) => (withNegativeMargin ? '-2px' : 0)};
  > svg {
    vertical-align: sub;
  }
`

const BedroomsWrapper = styled.div<{size: 'sm' | 'md'}>`
  display: flex;
  flex-wrap: wrap;
  row-gap: ${cssTheme.layout.spacing.s200};
  column-gap: ${cssTheme.layout.spacing.s300};
  align-items: center;
  ${({size}) => cssTheme.typography[size === 'sm' ? 'bodyS' : 'bodyM']}
`

const renderBedTypesWithIcons = ({
  group,
  size
}: {
  group: BedConfiguration[]
  size: 'sm' | 'md'
}) =>
  group.map(({type, count, typeID}, index) => {
    const iconName = getBedTypeIcon(typeID)
    return (
      <Box key={index} display="flex" alignItems="center">
        <StyledIcon name={iconName} size={size} withMargin={count !== 2} />
        {count == 2 ? (
          <StyledIcon
            name={iconName}
            size={size}
            withMargin
            withNegativeMargin
          />
        ) : null}
        {count}
        {count > 2 ? '×' : ''} {type}
      </Box>
    )
  })

export const BedGroup = ({
  group,
  size
}: {
  group: BedConfiguration[]
  size: 'sm' | 'md'
}) => {
  const {formatList} = useIntl()

  const bedTypes = renderBedTypesWithIcons({group, size})

  return (
    <Box display="flex" flexWrap="wrap" gap="s300" alignItems="center">
      {formatList(bedTypes)}
    </Box>
  )
}

export const InlineBedGroup = ({
  group,
  size
}: {
  group: BedConfiguration[]
  size: 'sm' | 'md'
}) => {
  const {formatList} = useIntl()

  const bedTypes = renderBedTypesWithIcons({group, size})

  return <>{formatList(bedTypes)}</>
}

const BedroomConfiguration = ({
  bedConfigurations,
  size
}: {
  bedConfigurations: BedConfiguration[]
  size: 'sm' | 'md'
}) => {
  const {formatList} = useIntl()

  const groupedByConfig = groupBedTypesByConfig(bedConfigurations)
  const bedGroups = groupedByConfig.map((group, idx) => (
    <BedGroup key={idx} group={group} size={size} />
  ))

  return (
    <BedroomsWrapper size={size}>
      {formatList(bedGroups, {type: 'disjunction'})}
    </BedroomsWrapper>
  )
}

export const Bedrooms = ({
  bedrooms,
  // Allows this component to be used on room card or room details overlay
  size = 'md'
}: {
  bedrooms: Bedroom[]
  size?: 'sm' | 'md'
}) => {
  if (!bedrooms?.length) return null
  return (
    <Box display="flex" flexWrap="wrap" columnGap="s300" alignItems="center">
      <DotSeparatedList
        listItems={bedrooms.map(({bedConfigurations}, idx) => (
          <BedroomConfiguration
            key={idx}
            size={size}
            bedConfigurations={bedConfigurations}
          />
        ))}
      />
    </Box>
  )
}
